import React, { useState, useEffect, useRef } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Get } from "../../config/api";
import { MESSAGE, UPLOADS_URL } from "../../config/constants/api";
import { useSelector } from "react-redux";

const ChatComponent = ({ id, data }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = useSelector((state) => state.user.userToken);
  const msgerChatRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadedPages, setLoadedPages] = useState(new Set());

  useEffect(() => {
    // Reset state when id changes
    setMessages([]);
    setPage(1);
    setHasMore(true);
    setLoadedPages(new Set());
    fetchMessages(1, true);
    // eslint-disable-next-line
  }, [id]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (msgerChatRef.current) {
      msgerChatRef.current.scrollTop = msgerChatRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchMessages = async (pageNum, isInitial = false) => {
    // Prevent loading the same page multiple times
    if (loadedPages.has(pageNum)) {
      return;
    }

    setLoading(true);
    Get(`${MESSAGE.getChatMessages}${id}`, token, { page: pageNum })
      .then((response) => {
        if (response?.status) {
          const newMessages = response.data.docs.reverse();
          
          if (newMessages.length < 25) setHasMore(false);
          
          if (isInitial) {
            // For initial load, replace all messages
            setMessages(newMessages);
          } else {
            // For pagination, merge and deduplicate
            setMessages((prevMessages) => {
              // Create a map of existing message IDs for quick lookup
              const existingIds = new Set(prevMessages.map(msg => msg._id));
              
              // Filter out duplicates from new messages
              const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg._id));
              
              // Prepend unique new messages to existing ones
              return [...uniqueNewMessages, ...prevMessages];
            });
          }
          
          setPage(pageNum);
          setLoadedPages(prev => new Set([...prev, pageNum]));
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log("Error fetching messages", err);
      });
  };
  
  const loadMoreMessages = () => {
    if (hasMore && !loading && !loadedPages.has(page + 1)) {
      fetchMessages(page + 1, false);
    }
  };

  const getAvatarSrc = (senderType) => {
    if (senderType === "user") {
      return data?.user?.image 
        ? `${UPLOADS_URL}${data.user.image}` 
        : "/assets/images/avatar.png";
    } else {
      return data?.chauffeur?.image 
        ? `${UPLOADS_URL}${data.chauffeur.image}` 
        : "/assets/images/avatar.png";
    }
  };

  const handleImageError = (e) => {
    e.target.src = "/assets/images/avatar.png";
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return 'Today';
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  return (
    <section 
      className="msger" 
      ref={chatContainerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '600px',
        maxHeight: '80vh',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#f9fafb',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}
    >
      <main 
        className="msger-chat" 
        ref={msgerChatRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          backgroundColor: '#f9fafb',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        {loading && messages.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '200px' 
          }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          </div>
        ) : messages.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px', 
            color: '#6b7280',
            fontFamily: 'Euclid Circular A, sans-serif',
            fontSize: '16px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
            <div>No messages yet. Start the conversation!</div>
          </div>
        ) : (
          <>
            {hasMore && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                padding: '8px 0',
                marginBottom: '8px'
              }}>
                <button
                  onClick={loadMoreMessages}
                  disabled={!hasMore || loading}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '20px',
                    padding: '6px 14px',
                    color: '#4b5563',
                    cursor: hasMore && !loading ? 'pointer' : 'not-allowed',
                    fontSize: '13px',
                    fontFamily: 'Euclid Circular A, sans-serif',
                    transition: 'all 0.2s',
                    opacity: hasMore && !loading ? 1 : 0.5,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                  onMouseEnter={(e) => {
                    if (hasMore && !loading) {
                      e.target.style.background = '#f3f4f6';
                      e.target.style.borderColor = '#d1d5db';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#ffffff';
                    e.target.style.borderColor = '#e5e7eb';
                  }}
                >
                  {loading ? (
                    <Spin
                      indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />}
                    />
                  ) : (
                    "Load More Messages"
                  )}
                </button>
              </div>
            )}
            {messages.map((msg, index) => {
              const isUser = msg.senderType === "user";
              const senderName = isUser 
                ? "You" 
                : data?.chauffeur?.firstName || "Chauffeur";

              return (
                <div
                  key={msg._id || index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    marginBottom: '12px',
                    flexDirection: isUser ? 'row-reverse' : 'row'
                  }}
                >
                  <img
                    alt={senderName}
                    src={getAvatarSrc(msg.senderType)}
                    onError={handleImageError}
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      objectPosition: "center",
                      flexShrink: 0,
                      border: '2px solid #ffffff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      backgroundColor: '#e5e7eb'
                    }}
                  />

                  <div 
                    style={{
                      maxWidth: '80%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isUser ? 'flex-end' : 'flex-start',
                      gap: '4px'
                    }}
                  >
                    <div 
                      style={{
                        fontSize: '13px',
                        fontWeight: 'bold',
                        color: '#1f2937',
                        fontFamily: 'Euclid Circular A, sans-serif',
                        paddingLeft: isUser ? '0' : '4px',
                        paddingRight: isUser ? '4px' : '0'
                      }}
                    >
                      {senderName}
                    </div>
                    <div 
                      style={{
                        padding: '14px 18px',
                        borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        backgroundColor: isUser ? '#d7b65d' : '#ffffff',
                        color: isUser ? '#ffffff' : '#1f2937',
                        fontSize: '15px',
                        lineHeight: '1.6',
                        fontFamily: 'Euclid Circular A, sans-serif',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        width: '100%',
                        minWidth: '200px',
                        textAlign: 'right'
                      }}
                    >
                      {msg.content}
                    </div>
                    <div 
                      style={{
                        fontSize: '10px',
                        color: '#9ca3af',
                        fontFamily: 'Euclid Circular A, sans-serif',
                        fontWeight: 400,
                        paddingLeft: isUser ? '0' : '4px',
                        paddingRight: isUser ? '4px' : '0',
                        textAlign: 'right'
                      }}
                    >
                      {formatDate(msg.createdAt)} · {formatTime(msg.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </main>
    </section>
  );
};

export default ChatComponent;
